/**
 * Two-step delete confirmation for /v1/admin/devices/{id}.
 *
 * Step 1: confirm the destructive action with explicit copy.
 * Step 2: if the API returns 409 (`device_in_use` — there are still
 *         meters attached) we *do not* surface a generic error toast.
 *         Instead we morph the modal in-place into a "retire instead"
 *         prompt with a one-click path that PATCHes status="retired".
 *         This mirrors the meter flow and matches §9.3's stance that
 *         retire is the everyday operator tool when a device cannot
 *         be cleanly deleted.
 *
 * Notes vs. `MeterDeleteConfirm`:
 *   - the device API has only one stable 409 code (`device_in_use`),
 *     so the conflict branch is unconditional once we see 409,
 *   - there's no `meter_external_refs` analog for devices, so we
 *     skip the second guidance variant and go straight to the
 *     "detach the meters first or retire" copy,
 *   - the meter_count from the row is included so the operator
 *     sees how many meters they need to detach.
 */
import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconArchive } from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import { useDeleteDevice, useUpdateDevice } from "@/api/queries";
import type { AdminDevice } from "@/api/types";

export type DeviceDeleteConfirmProps = {
  opened: boolean;
  onClose: () => void;
  device: AdminDevice | null;
  onSuccess?: () => void;
};

type RetirableConflict = {
  code: "device_in_use";
  message: string;
};

function asRetirableConflict(error: unknown): RetirableConflict | null {
  if (error instanceof ApiError && error.status === 409 && error.code === "device_in_use") {
    return { code: error.code, message: error.message };
  }
  return null;
}

export function DeviceDeleteConfirm({
  opened,
  onClose,
  device,
  onSuccess,
}: DeviceDeleteConfirmProps) {
  const deleteMutation = useDeleteDevice();
  const retireMutation = useUpdateDevice();
  const [conflict, setConflict] = useState<RetirableConflict | null>(null);

  // Reset transient state when the modal is reopened against a new target.
  useEffect(() => {
    if (opened) {
      setConflict(null);
      deleteMutation.reset();
      retireMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, device?.id]);

  if (!device) return null;

  const isAlreadyRetired = device.status === "retired";

  const handleDelete = async () => {
    setConflict(null);
    try {
      await deleteMutation.mutateAsync(device.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      const c = asRetirableConflict(err);
      if (c) {
        setConflict(c);
        return;
      }
      // anything else stays visible through deleteMutation.error below
    }
  };

  const handleRetire = async () => {
    try {
      await retireMutation.mutateAsync({
        id: device.id,
        body: { status: "retired" },
      });
      onSuccess?.();
      onClose();
    } catch {
      // keep the modal open and surface retireMutation.error inline
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={conflict ? "Retire this device instead?" : "Delete device"}
      centered
      size="md"
    >
      <Stack>
        {conflict ? (
          <RetireInsteadPrompt device={device} />
        ) : (
          <Stack gap="xs">
            <Text>
              Permanently delete{" "}
              <Text component="span" fw={600}>
                {device.display_name}
              </Text>{" "}
              ({device.site_slug}/{device.slug})?
            </Text>
            <Text c="dimmed" size="sm">
              This is only allowed if no meters are still attached. For most
              physical assets, retire (status →
              <Text component="span" ff="monospace">
                {" retired"}
              </Text>
              ) is almost always the right call — it hides the device from
              operator views without orphaning its channels.
            </Text>
          </Stack>
        )}

        {!conflict && deleteMutation.error && !asRetirableConflict(deleteMutation.error) && (
          <Alert color="red" variant="light" title="Delete failed">
            {deleteMutation.error instanceof ApiError
              ? `${deleteMutation.error.code}: ${deleteMutation.error.message}`
              : "Unknown error"}
          </Alert>
        )}
        {retireMutation.error && (
          <Alert color="red" variant="light" title="Retire failed">
            {retireMutation.error instanceof ApiError
              ? `${retireMutation.error.code}: ${retireMutation.error.message}`
              : "Unknown error"}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          {!conflict ? (
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={handleDelete}
            >
              Delete
            </Button>
          ) : (
            !isAlreadyRetired && (
              <Button
                color="orange"
                leftSection={<IconArchive size={16} />}
                loading={retireMutation.isPending}
                onClick={handleRetire}
              >
                Retire instead
              </Button>
            )
          )}
        </Group>
      </Stack>
    </Modal>
  );
}

function RetireInsteadPrompt({ device }: { device: AdminDevice }) {
  const isAlreadyRetired = device.status === "retired";
  const icon = <IconAlertTriangle size={18} />;

  return (
    <Alert
      color="orange"
      variant="light"
      icon={icon}
      title="Meters are still attached to this device"
    >
      <Stack gap="xs">
        <Text size="sm">
          <Text component="span" fw={600}>
            {device.display_name}
          </Text>{" "}
          still has {device.meter_count} meter{device.meter_count === 1 ? "" : "s"}{" "}
          attached. Detach them via{" "}
          <Text component="span" ff="monospace">
            PATCH /v1/admin/sites/&#123;site_slug&#125;/meters/&#123;meter_slug&#125;/device
          </Text>{" "}
          (cookbook §9.3) before retrying the delete.
        </Text>
        <Text size="sm">
          For most operator workflows, retiring the device is enough — the
          channels stay in place and the worker keeps writing to a
          <Text component="span" ff="monospace"> retired </Text> device
          without complaining.
        </Text>
        {isAlreadyRetired && (
          <Text size="sm" c="dimmed">
            Status is already <Text component="span" ff="monospace">retired</Text>.
            No further action is needed; close this dialog.
          </Text>
        )}
      </Stack>
    </Alert>
  );
}
